// =============================================================================
// VINUM - Badge Component
// Migrated from Flutter status chips with EXACT colors
// =============================================================================

import React from 'react';
import { cn } from '@/lib/cn';
import type { OrderStatus } from '@vinum/shared';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md';
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', children, ...props }, ref) => {
    const variantStyles = {
      default: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      primary: 'bg-primary/10 text-primary',
      secondary: 'bg-secondary/10 text-secondary',
      success: 'bg-success/10 text-success',
      warning: 'bg-warning/10 text-warning',
      error: 'bg-error/10 text-error',
      info: 'bg-info/10 text-info',
    };

    const sizeStyles = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-sm',
    };

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center font-medium rounded-full',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// =============================================================================
// Order Status Badge
// Exact match with Flutter _StatusBadge colors
// =============================================================================

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: OrderStatus;
  size?: 'sm' | 'md';
}

const STATUS_STYLES: Record<OrderStatus, string> = {
  'In Attesa': 'bg-warning/10 text-warning',
  'Confermato': 'bg-success/10 text-success',
  'Rifiutato': 'bg-error/10 text-error',
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  'In Attesa': 'In Attesa',
  'Confermato': 'Confermato',
  'Rifiutato': 'Rifiutato',
};

export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ status, size = 'md', className, ...props }, ref) => {
    const sizeStyles = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-sm',
    };

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center font-medium rounded-full',
          STATUS_STYLES[status],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {STATUS_LABELS[status]}
      </span>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';

// =============================================================================
// Stock Badge
// Exact match with Flutter _stockChip
// =============================================================================

export interface StockBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  quantity: number;
  size?: 'sm' | 'md';
}

export const StockBadge = React.forwardRef<HTMLSpanElement, StockBadgeProps>(
  ({ quantity, size = 'md', className, ...props }, ref) => {
    const inStock = quantity > 0;

    const sizeStyles = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-sm',
    };

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center font-semibold rounded-full',
          inStock
            ? 'bg-success/10 text-success'
            : 'bg-error/10 text-error',
          sizeStyles[size],
          className
        )}
        {...props}
      >
        Giacenza: {quantity}
      </span>
    );
  }
);

StockBadge.displayName = 'StockBadge';
